import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TransitionGroup } from 'react-transition-group';
import { Typography, Fade, makeStyles } from '@material-ui/core';
import OneOnOneTimeIndicator from '../oneOnOneTimeIndicator';
import Impression from '../impressions/impression';
import DotsMenu from '../dotsMenu';
import AgendaList from '../agendaList';
import Tag from '../tag';
import OneOnOneCalendar from '../oneOnOneCalendar';
import RecurringIconTooltip from '../recuringIconTooltip';
import TasksList from '../tasksList';
import RequestedIconTooltip from '../requestedIconTooltip';
import UserLink from '../userLink';
import TextLink from '../textLink';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import ActionButton from '../actionButton';
import Tooltip from '../tooltip';
import UserCount from '../userCount';
import { ReactComponent as PreviewUserIcon } from '../../../assets/icons/remove_red_eye.svg';
import {
  DATE_FORMAT,
  RECURRING_TYPES_MAP,
  TIME_FORMAT,
} from '../../../constants/oneOnOne';
import { api_one_on_one_with_view_permission } from '../../../constants/apiRoutes';
import {
  getPersonalAttributeTooltipText,
  getPersonFullName,
} from '../../../utility/uiUtils';
import { isArrayEmpty } from '../../../utility/helpers';
import { formatDate, isDateInFuture } from '../../../utility/dateUtils';
import {
  prepareDotsMenuItems,
  prepareUserAgenda,
  prepareModeratorAgenda,
} from '../../../utility/oneOnOne';
import http from '../../../utility/http';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    boxSizing: 'border-box',
    backgroundColor: primary.bluish9,
    borderRadius: 4,
    display: 'grid',
    gridTemplateColumns: '1fr 225px',
    minHeight: 150,
  },
  content: {
    borderRight: `1px dashed ${primary.bluish7}`,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gridGap: 16,
    padding: spacing(6, 4, 6, 6),
  },
  info: {
    position: 'relative',
    padding: spacing(6, 6, 6, 4),
  },
  label: {
    color: primary.bluish3,
    fontSize: 14,
    fontFamily: 'ProximaNova-Regular',
    marginBottom: spacing(1),
  },
  userWrapper: {
    display: 'flex',
  },
  user: {
    height: 46,
  },
  userRequested: {
    maxWidth: 'calc(100% - 30px)',
  },
  requested: {
    marginTop: spacing(5),
    marginLeft: spacing(1.5),
  },
  dateWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing(1),
    height: 24,
  },
  date: {
    marginRight: spacing(1.5),
  },
  time: {
    marginBottom: spacing(4),
  },
  impressionWrapper: {
    marginTop: spacing(4),
  },
  impressionLabel: {
    fontFamily: 'ProximaNova-Bold',
    fontSize: 12,
    lineHeight: '14px',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: spacing(2),
  },
  impressionValue: {
    cursor: 'default',
  },
  menu: {
    position: 'absolute',
    right: 20,
    top: 24,
  },
  userCount: {
    position: 'absolute',
    right: 18.5,
    bottom: 20,
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing(2),
  },
  iconTooltip: {
    marginLeft: spacing(1.5),
  },
  actionsWrapper: {
    display: 'flex',
  },
  rescheduleWrapper: {
    position: 'absolute',
    top: 22,
    right: 50,
  },
  fullWidth: {
    gridColumn: 'span 2',
  },
  tagsWrapper: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    width: '100%',
  },
  impression: {
    display: 'flex',
  },
  impressionTooltip: {
    width: 12,
    height: 12,
    marginLeft: spacing(1),
  },
  tagsWrapperWithoutTags: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 28,
  },
  tag: {
    margin: spacing(0, 2, 2, 0),
  },
  plansWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  planWrapper: {
    display: 'flex',
    marginBottom: spacing(2),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
}));

const OneOnOneRecordDetails = ({
  translations,
  record,
  canEdit,
  canDelete,
  canViewPlans,
  isUserActive,
  isCurrentUserSubject,
  isCurrentUserModerator,
  isPendingRequest,
  checkCanAccessUserProfile,
  onAddImpression,
  onReschedule,
  onManageTasks,
  onEditRecord,
  onEditEmployeeTopic,
  onDeleteRecord,
  onTaskStatusChange,
  checkCanManageTask,
  goToUserProfilePage,
}) => {
  const classes = useStyles();
  const {
    user,
    moderator,
    meeting_time,
    meeting_impression,
    has_impression,
    requested,
    tags,
    recurring,
    tasks,
    plans,
  } = record;
  const { RESOLVE, RESCHEDULE } = ACTION_BUTTON_TYPES;
  const isUserAccessible = checkCanAccessUserProfile(user?.id);
  const isModeratorAccessible =
    !!moderator && checkCanAccessUserProfile(moderator?.id);
  const hasActions = !has_impression && canEdit && isUserActive;
  const isFutureOneOnOne = isDateInFuture(meeting_time);
  const isRecurring = recurring !== RECURRING_TYPES_MAP.DOES_NOT_REPEAT.id;
  const hasAddToCalendarButton =
    !isPendingRequest && isFutureOneOnOne && (canEdit || isCurrentUserSubject);
  const calendarTarget = getPersonFullName(
    isCurrentUserSubject ? moderator : user
  );
  const hasPlans = !isArrayEmpty(plans);
  const canManageUserTopic = isFutureOneOnOne && isCurrentUserSubject;
  const hasDotsMenu =
    !isPendingRequest &&
    (canEdit || canDelete || isCurrentUserModerator || isCurrentUserSubject);

  const handleTaskStatusChange = useCallback(
    task => {
      return onTaskStatusChange(record, task);
    },
    [record, onTaskStatusChange]
  );

  const getUsersWithViewPermission = recordId => () =>
    http.get(api_one_on_one_with_view_permission(recordId));

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <div className={classes.userWrapper}>
          <UserLink
            className={classNames(classes.user, {
              [classes.userRequested]: requested,
            })}
            labelClass={classes.label}
            label={translations.with}
            user={user}
            isUserAccessible={isUserAccessible}
          />
          {requested && (
            <RequestedIconTooltip
              className={classes.requested}
              labels={translations.requested}
              isRequestedByCurrentUser={isCurrentUserSubject}
            />
          )}
        </div>
        <div>
          <UserLink
            className={classes.user}
            labelClass={classes.label}
            label={translations.moderator}
            user={moderator}
            isUserAccessible={isModeratorAccessible}
          />
        </div>
        <div>
          <AgendaList
            titleClass={classes.label}
            title={translations.managerAgenda}
            items={prepareModeratorAgenda(record)}
          />
        </div>
        <div>
          <AgendaList
            titleClass={classes.label}
            title={translations.employeeAgenda}
            items={prepareUserAgenda(record)}
          />
        </div>
        <div className={classes.fullWidth}>
          <Typography className={classes.label} variant="body2">
            {translations.tags}
          </Typography>
          <TransitionGroup className={classes.tagsWrapper}>
            {tags.map(tag => (
              <Fade key={`tag_item_${tag.id}`} in>
                <div className={classes.tag}>
                  <Tag tag={tag} color={tag.category.color} isSelected />
                </div>
              </Fade>
            ))}
          </TransitionGroup>
          <Fade in={isArrayEmpty(tags)} unmountOnExit>
            <Typography className={classes.tagsWrapperWithoutTags}>
              -
            </Typography>
          </Fade>
        </div>
        <div className={classes.fullWidth}>
          <Typography className={classes.label} variant="body2">
            {translations.tasks}
          </Typography>
          <TasksList
            translations={translations.tasksList}
            tasks={tasks}
            canAccessUserProfile={checkCanAccessUserProfile}
            checkCanManageTask={checkCanManageTask}
            onStatusChange={handleTaskStatusChange}
          />
          <Fade in={isArrayEmpty(tasks)} unmountOnExit>
            <Typography variant="body2">-</Typography>
          </Fade>
        </div>
        <div className={classes.fullWidth}>
          <Typography
            className={classNames(classes.label, classes.labelSpacing)}
            variant="body2"
          >
            {translations.associatedPlans}
          </Typography>
          <Fade in={!hasPlans} unmountOnExit>
            <Typography variant="body2">-</Typography>
          </Fade>
          <TransitionGroup className={classes.plansWrapper}>
            {plans.map(plan => (
              <Fade
                key={`plan_item_${plan.id}`}
                className={classes.planWrapper}
                in
                unmountOnExit
              >
                <div>
                  {canViewPlans ? (
                    <TextLink
                      to={`/action-plans/${plan.id}/details`}
                      variant="subtitle1"
                      text={plan.title}
                    />
                  ) : (
                    <TextBoxWithTooltip variant="subtitle1" text={plan.title} />
                  )}
                </div>
              </Fade>
            ))}
          </TransitionGroup>
        </div>
      </div>
      <div className={classes.info}>
        <div className={classes.status}>
          <OneOnOneTimeIndicator
            labels={translations.statuses}
            meetingTime={meeting_time}
            hasImpression={has_impression}
          />
          <RecurringIconTooltip
            className={classes.iconTooltip}
            isVisible={isRecurring}
            text={translations.recurring}
          />
          {!isPendingRequest && !isFutureOneOnOne && hasActions && canEdit && (
            <div
              className={classNames(
                classes.actionsWrapper,
                classes.rescheduleWrapper
              )}
            >
              <ActionButton
                type={RESCHEDULE}
                tooltipText={
                  translations.actionButtons[RESCHEDULE.toLowerCase()]
                }
                onClickHandler={onReschedule}
                isSquared
              />
            </div>
          )}
        </div>
        <div className={classes.dateWrapper}>
          <Typography variant="subtitle1" className={classes.date}>
            {formatDate(meeting_time, DATE_FORMAT)}
          </Typography>
          {hasAddToCalendarButton ? (
            <OneOnOneCalendar
              translations={translations.calendar}
              record={{ ...record, targetFullName: calendarTarget }}
              isSmall
            />
          ) : null}
        </div>
        <Typography className={classes.time} variant="caption">
          {formatDate(meeting_time, TIME_FORMAT)}
        </Typography>
        {!isPendingRequest && !isFutureOneOnOne && (
          <div className={classes.impressionWrapper}>
            {(hasActions || !has_impression) && !isCurrentUserSubject && (
              <div className={classes.impression}>
                <Typography
                  variant="overline"
                  className={classNames(classes.label, classes.impressionLabel)}
                >
                  {translations.impression}
                </Typography>
                <Tooltip
                  customIconClass={classNames(
                    classes.impressionTooltip,
                    'no-print'
                  )}
                  text={getPersonalAttributeTooltipText(
                    translations.impressionTooltip,
                    user
                  )}
                />
              </div>
            )}
            {hasActions && !isCurrentUserSubject && (
              <div className={classes.actionsWrapper}>
                <ActionButton
                  type={RESOLVE}
                  tooltipText={
                    translations.actionButtons[RESOLVE.toLowerCase()]
                  }
                  onClickHandler={onAddImpression}
                  isSquared
                />
              </div>
            )}
            {has_impression && !isCurrentUserSubject && (
              <Impression
                className={classes.impressionValue}
                value={meeting_impression}
                isSmall
              />
            )}
          </div>
        )}
        <UserCount
          className={classNames(classes.userCount, 'no-print')}
          icon={PreviewUserIcon}
          tooltipTitle={translations.usersListTitle}
          tooltipLabel={translations?.usersListTooltip}
          goToUserProfile={goToUserProfilePage}
          canAccessUserProfileCheck={checkCanAccessUserProfile}
          getUsers={getUsersWithViewPermission(record.id)}
          isPreviewIcon
          hasUserCount={false}
        />
        {hasDotsMenu && (
          <DotsMenu
            className={classNames(classes.menu, 'no-print')}
            menuItems={prepareDotsMenuItems(
              translations,
              isCurrentUserModerator,
              isCurrentUserSubject,
              !isUserActive,
              canEdit,
              canDelete,
              canManageUserTopic,
              onEditRecord,
              onDeleteRecord,
              onManageTasks,
              onEditEmployeeTopic
            )}
          />
        )}
      </div>
    </div>
  );
};

OneOnOneRecordDetails.propTypes = {
  translations: PropTypes.object.isRequired,
  record: PropTypes.object.isRequired,
  canEdit: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool.isRequired,
  canViewPlans: PropTypes.bool.isRequired,
  isUserActive: PropTypes.bool.isRequired,
  isCurrentUserModerator: PropTypes.bool.isRequired,
  isCurrentUserSubject: PropTypes.bool.isRequired,
  isPendingRequest: PropTypes.bool.isRequired,
  checkCanAccessUserProfile: PropTypes.func.isRequired,
  onDeleteRecord: PropTypes.func.isRequired,
  onManageTasks: PropTypes.func.isRequired,
  onAddImpression: PropTypes.func.isRequired,
  onReschedule: PropTypes.func.isRequired,
  checkCanManageTask: PropTypes.func.isRequired,
  onTaskStatusChange: PropTypes.func.isRequired,
  onEditEmployeeTopic: PropTypes.func.isRequired,
  goToUserProfilePage: PropTypes.func.isRequired,
};

export default OneOnOneRecordDetails;
